/**
 * Freshness resolution (seam S8, invariant I-6).
 *
 * The whole point of this file: **unknown is a state, not a blank.** A module
 * response with no `meta`, or with a date we cannot parse, renders "we can't
 * confirm this data's date" — never nothing, and never a date we inferred. A
 * silently-stale number acted on is the portal's worst failure mode (BR-11,
 * UX v0.3 §2 action 2), so the derivation lives here as pure functions and is
 * tested directly rather than through a component.
 *
 * The shell derives normal / stale / unknown from `meta` ALONE. A module never
 * tells us it is fresh; it tells us its date and its expected max age, and we
 * do the arithmetic.
 */

import type { FreshnessMeta } from "./api";
import { formatUtcStamp } from "./clock";

export type FreshnessKind = "normal" | "stale" | "unknown";

export interface Freshness {
  kind: FreshnessKind;
  /** The rendered sentence — UX v0.3 §7 microcopy, verbatim. */
  text: string;
  /** Present only when we could actually read a date. */
  dataAsOf: string | null;
  source: string | null;
}

const UNKNOWN: Freshness = {
  kind: "unknown",
  text: "We can't confirm this data's date.",
  dataAsOf: null,
  source: null,
};

/**
 * Resolve the freshness of one module response.
 *
 * `now` is injected so the stale boundary is testable without freezing clocks.
 */
export function resolveFreshness(
  meta: FreshnessMeta | undefined | null,
  now: number = Date.now(),
): Freshness {
  if (!meta || !meta.data_as_of) return UNKNOWN;

  const asOf = Date.parse(meta.data_as_of);
  // An unparseable date is NOT a date. Rendering the raw string would present
  // garbage as a fact; falling through to unknown states what is true.
  if (Number.isNaN(asOf)) return UNKNOWN;

  const source = meta.source ?? null;
  const shown = formatAsOf(meta.data_as_of);
  // The source (e.g. a bucket or dataset name) is an engineer's detail, not a
  // reader's: it stays on `source` for the line's tooltip, off the sentence.
  const head = `Data to ${shown}`;

  // No declared expectation means we cannot call it stale — we can only report
  // the date. Guessing a threshold would invent a judgement the module owner
  // never made.
  if (meta.expected_max_age_hours == null) {
    return { kind: "normal", text: head, dataAsOf: meta.data_as_of, source };
  }

  const ageHours = (now - asOf) / 3_600_000;
  if (ageHours > meta.expected_max_age_hours) {
    return {
      kind: "stale",
      text: `${head} · older than expected`,
      dataAsOf: meta.data_as_of,
      source,
    };
  }
  return { kind: "normal", text: head, dataAsOf: meta.data_as_of, source };
}

/**
 * Render the date as the module stated it: a date-only value stays date-only,
 * a timestamp keeps its time, in UTC and labelled so (the shared `clock.ts`
 * formatter module status lines use too). Widening a date into a timestamp
 * would imply a precision the module did not claim.
 */
function formatAsOf(raw: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return formatUtcStamp(raw) || raw;
}

/** The backend-down line (UX v0.3 §7). Named here so both users share one string. */
export function backendDownCopy(system: string, lastLoad: string | null): string {
  return lastLoad
    ? `We can't reach ${system} right now. Last successful load: ${lastLoad}.`
    : `We can't reach ${system} right now.`;
}
